import GeneralDialog from '../../../shared/general-dialog';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { GmailAuditModel } from '../../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { UserKeys, createGmailExport } from '../../../api/users-api';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedAutocomplete from '../../../shared/shared-autocomplete';
import {
  DateRange,
  PickersShortcutsItem,
  SingleInputDateTimeRangeField,
} from '@mui/x-date-pickers-pro';
import { renderDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import dayjs, { Dayjs } from 'dayjs';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface CreateGmailExportDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
}

export interface CreateGmailExportForm extends Partial<GmailAuditModel> {
  dateRange?: DateRange<Dayjs>;
  destinationUserEmail?: string;
}

export default function CreateGmailExportDialog({
  open,
  handleClose,
  changeLogRoles,
}: CreateGmailExportDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: 'Last Week',
      getValue: () => {
        const today = dayjs();
        const prevWeek = today.subtract(7, 'day');
        return [prevWeek.startOf('week'), prevWeek.endOf('week')];
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, 'day'), today];
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        const today = dayjs();
        const prevMonth = today.subtract(1, 'month');
        return [prevMonth.startOf('month'), prevMonth.endOf('month')];
      },
    },
    { label: 'Clear', getValue: () => [null, null] },
  ];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    watch,
    setValue,
  } = useForm<CreateGmailExportForm>({
    defaultValues: {
      dateRange: [null, null],
      user: '',
      searchQuery: '',
      headerOnly: false,
      includeDeleted: false,
      downloadUrls: [],
    },
  });

  const watchUser = watch('user') || '';
  const watchIncludeDeleted = watch('includeDeleted') || false;

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<GmailAuditModel>) =>
      createGmailExport(data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Gmail export created successfully!',
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GmailAuditModels],
      });
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating this gmail export. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.gmail_export.create_gmail_export, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.gmailExport,
      });
    },
  });

  const onSubmit: SubmitHandler<CreateGmailExportForm> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CreateGmailExports.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newModel: CreateGmailExportForm = {
        ...data,
        beginDate: data.dateRange
          ? data.dateRange[0]?.toDate().getTime()
          : undefined,
        endDate: data.dateRange
          ? data.dateRange[1]?.toDate().getTime()
          : undefined,
        searchQuery: data.includeDeleted ? '' : data.searchQuery,
      };
      delete newModel.dateRange;
      delete newModel.destinationUserEmail;
      mutate(newModel);
    }
  };

  return (
    <GeneralDialog
      maxWidth="sm"
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Create Gmail Export"
      dialogContent={
        <Stack sx={{ px: 1 }}>
          <Typography sx={{ mb: 2 }}>
            An export creates an encrypted copy of a user's mailbox which can be
            downloaded and decrypted in mbox format. More information can be
            found{' '}
            <Link
              href="https://developers.google.com/workspace/admin/email-audit/download-mailbox"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </Link>
            .
          </Typography>
          <Controller
            name={'user'}
            control={control}
            rules={{
              required: 'Please supply a user to export.',
            }}
            render={() => (
              <Box sx={{ flexGrow: 1 }}>
                <SharedAutocomplete
                  sources={['users']}
                  label={'User to Export'}
                  autocompleteValue={watchUser}
                  setAutoCompleteValue={(value) => {
                    setValue('user', value);
                  }}
                  errors={!!errors.user}
                  helperText={errors.user?.message ? errors.user.message : ' '}
                />
              </Box>
            )}
          />
          <Controller
            name={'dateRange'}
            control={control}
            render={({ field }) => (
              <DateTimeRangePicker
                sx={{
                  flexGrow: 1,
                  mt: 1,
                  mb: 4,
                }}
                label={'Date Range'}
                views={['day', 'hours']}
                timeSteps={{ minutes: 5 }}
                viewRenderers={{ hours: renderDigitalClockTimeView }}
                {...field}
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems,
                  },
                  actionBar: { actions: ['accept'] },
                  // to avoid offscreen of range pop up in dialog
                  popper: {
                    placement: 'top',
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -40],
                        },
                      },
                    ],
                  },
                }}
                slots={{ field: SingleInputDateTimeRangeField }}
              />
            )}
          />
          <Stack flexDirection="row" mb={1}>
            <Controller
              name="includeDeleted"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      onChange={(e) => {
                        props.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Include Deleted Messages"
                />
              )}
            />
            {!watchIncludeDeleted ? (
              <Controller
                name="searchQuery"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'searchQuery',
                    }}
                    InputLabelProps={{
                      htmlFor: 'searchQuery',
                    }}
                    label="Search Query"
                    {...field}
                    sx={{ flexGrow: 1 }}
                    autoComplete="off"
                  />
                )}
              />
            ) : null}
          </Stack>
          <Controller
            name={'headerOnly'}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue={false}
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    row
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="Full Message"
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Headers Only"
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const formData = {
                ...getValues(),
              };
              const newExport: CreateGmailExportForm = {
                ...formData,
                beginDate: formData.dateRange
                  ? formData.dateRange[0]?.toDate().getTime()
                  : undefined,
                endDate: formData.dateRange
                  ? formData.dateRange[1]?.toDate().getTime()
                  : undefined,
                searchQuery: formData.includeDeleted
                  ? ''
                  : formData.searchQuery,
              };
              delete newExport.dateRange;
              mutate(newExport);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
